import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  Chip,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Send as SendIcon,
  LocalCafe as CoffeeIcon,
  ContentCut as SalonIcon,
  FitnessCenter as GymIcon,
  MedicalServices as DentistIcon,
  HomeWork as RealEstateIcon,
  EmojiEmotions,
  BusinessCenter,
  Bolt,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

// Business types definition
const businesses = [
  {
    id: 'DEMO_COFFEE',
    name: 'Morning Brew',
    type: 'Coffee Shop',
    icon: <CoffeeIcon />,
    color: '#6F4E37',
    secondaryColor: '#C4A484',
    welcomeMessage: 'Hey there! ☕ Need a caffeine fix? I can take your order for pickup!',
  },
  {
    id: 'DEMO_SALON',
    name: 'Luxe Nails',
    type: 'Nail Salon',
    icon: <SalonIcon />,
    color: '#FF69B4',
    secondaryColor: '#FFC0CB',
    welcomeMessage: 'Welcome to Luxe Nails! 💅 Ready to book your next pampering session?',
  },
  {
    id: 'DEMO_GYM',
    name: 'Iron Clad',
    type: 'Gym',
    icon: <GymIcon />,
    color: '#FF4500',
    secondaryColor: '#FFA500',
    welcomeMessage: 'Let\'s crush those goals! 💪 Need to check class times or membership info?',
  },
  {
    id: 'DEMO_DENTIST',
    name: 'Bright Smiles',
    type: 'Dentist',
    icon: <DentistIcon />,
    color: '#008080',
    secondaryColor: '#40E0D0',
    welcomeMessage: 'Hello. I can help you schedule your next cleaning or checkup. 🦷',
  },
  {
    id: 'DEMO_REAL_ESTATE',
    name: 'Prime Estates',
    type: 'Real Estate',
    icon: <RealEstateIcon />,
    color: '#2F4F4F',
    secondaryColor: '#708090',
    welcomeMessage: 'Greetings. Looking for your dream home? I can help you with listings. 🏠',
  },
];

const tones = [
  { value: 'friendly', label: 'Friendly', icon: <EmojiEmotions fontSize="small" /> },
  { value: 'professional', label: 'Professional', icon: <BusinessCenter fontSize="small" /> },
  { value: 'concise', label: 'Concise', icon: <Bolt fontSize="small" /> },
];

interface Message {
  id: string;
  sender: 'customer' | 'ai';
  text: string;
  timestamp: Date;
}

export const LiveDemo: React.FC = () => {
  const [selectedBusiness, setSelectedBusiness] = useState(businesses[0]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messageCount, setMessageCount] = useState(20);
  const [selectedTone, setSelectedTone] = useState('friendly');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [sessionId] = useState(`demo-${Date.now()}`);

  // Initialize with welcome message when business changes
  useEffect(() => {
    setMessages([
      {
        id: 'welcome',
        sender: 'ai',
        text: selectedBusiness.welcomeMessage,
        timestamp: new Date(),
      },
    ]);
    setMessageCount(20);
  }, [selectedBusiness]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || messageCount <= 0 || isTyping) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      sender: 'customer',
      text: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, newMessage]);
    setInputValue('');
    setIsTyping(true);
    setMessageCount((prev) => prev - 1);

    try {
      // Ensure API URL never includes /v1
      let apiUrl = process.env.REACT_APP_API_ENDPOINT || 
        'https://hep8t7i0l7.execute-api.us-east-1.amazonaws.com/prod';
      // For custom domains, remove /v1. For API Gateway, keep /prod
      if (apiUrl.includes('api.reachoutaiapp.com')) {
        apiUrl = apiUrl.replace(/\/v1\/?$/, '');
      } else if (apiUrl.includes('execute-api')) {
        apiUrl = apiUrl.replace(/\/+$/, '');
        if (!apiUrl.endsWith('/prod')) {
          apiUrl = apiUrl + '/prod';
        }
      }
      const response = await fetch(`${apiUrl}/demo/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendorId: selectedBusiness.id,
          message: newMessage.text,
          sessionId: sessionId,
          tone: selectedTone, // Pass selected tone to backend
        }),
      });

      const data = await response.json();

      if (data.success) {
        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          sender: 'ai',
          text: data.data.response,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, aiMessage]);
      } else {
        // Fallback or error handling
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          sender: 'ai',
          text: "I'm having a little trouble connecting right now. Try again in a moment!",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Demo chat error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: "Oops! Connection issue. Please check your internet.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleToneChange = (event: React.MouseEvent<HTMLElement>, newTone: string | null) => {
    if (newTone !== null) {
      setSelectedTone(newTone);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto', p: 2 }}>
      <Grid container spacing={4}>
        {/* Business Selector (Left Side) */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" fontWeight={700} color="white" mb={1}>
              1. Pick a Business
            </Typography>
            {businesses.map((business) => (
              <motion.div
                key={business.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Paper
                  onClick={() => setSelectedBusiness(business)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    bgcolor: selectedBusiness.id === business.id ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                    border: `1px solid ${selectedBusiness.id === business.id ? business.color : 'rgba(255, 255, 255, 0.1)'}`,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Avatar sx={{ bgcolor: business.color }}>{business.icon}</Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} color="white">
                      {business.name}
                    </Typography>
                    <Typography variant="caption" color="rgba(255, 255, 255, 0.6)">
                      {business.type}
                    </Typography>
                  </Box>
                  {selectedBusiness.id === business.id && (
                    <Box
                      sx={{
                        ml: 'auto',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: business.color,
                        boxShadow: `0 0 10px ${business.color}`,
                      }}
                    />
                  )}
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Grid>

        {/* Chat Interface (Right Side) */}
        <Grid item xs={12} md={8}>
          <Box sx={{ mb: 2 }}>
             <Typography variant="h6" fontWeight={700} color="white" mb={1}>
              2. Choose AI Tone
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 3,
                display: 'inline-flex',
              }}
            >
              <ToggleButtonGroup
                value={selectedTone}
                exclusive
                onChange={handleToneChange}
                aria-label="ai tone"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'rgba(255, 255, 255, 0.6)',
                    borderColor: 'transparent',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2,
                    py: 0.5,
                    mx: 0.5,
                    border: 'none',
                    '&.Mui-selected': {
                      bgcolor: selectedBusiness.color,
                      color: 'white',
                      '&:hover': {
                        bgcolor: selectedBusiness.secondaryColor,
                      },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  },
                }}
              >
                {tones.map((tone) => (
                  <ToggleButton key={tone.value} value={tone.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tone.icon}
                      <Typography variant="body2" fontWeight={600}>{tone.label}</Typography>
                    </Box>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Paper>
          </Box>

          <Paper
            elevation={0}
            sx={{
              height: 550,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 4,
              overflow: 'hidden',
              bgcolor: 'rgba(20, 25, 45, 0.9)',
              border: `1px solid ${selectedBusiness.color}40`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 20px 60px -10px ${selectedBusiness.color}20`,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2.5,
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'rgba(0, 0, 0, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: selectedBusiness.color }}>{selectedBusiness.icon}</Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="white">
                    {selectedBusiness.name} AI
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#00C896' }} />
                    <Typography variant="caption" color="rgba(255, 255, 255, 0.6)">
                      Online • {selectedTone.charAt(0).toUpperCase() + selectedTone.slice(1)} Mode
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Chip
                label={`${messageCount} messages left`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  borderWidth: 1,
                  borderStyle: 'solid',
                }}
              />
            </Box>

            {/* Messages Area */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <AnimatePresence initial={false}>
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      alignSelf: message.sender === 'customer' ? 'flex-end' : 'flex-start',
                      maxWidth: '80%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: message.sender === 'customer' ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          bgcolor: message.sender === 'customer' ? selectedBusiness.color : 'rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          borderRadius: 3,
                          borderBottomRightRadius: message.sender === 'customer' ? 4 : 24,
                          borderBottomLeftRadius: message.sender === 'ai' ? 4 : 24,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        }}
                      >
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          {message.text}
                        </Typography>
                      </Paper>
                      <Typography variant="caption" color="rgba(255, 255, 255, 0.4)" sx={{ mt: 0.5, px: 1 }}>
                        {message.sender === 'ai' ? 'AI Assistant' : 'You'}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </AnimatePresence>

              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{ alignSelf: 'flex-start' }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      borderBottomLeftRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      component={motion.div}
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                      sx={{ width: 6, height: 6, bgcolor: 'rgba(255, 255, 255, 0.6)', borderRadius: '50%' }}
                    />
                    <Box
                      component={motion.div}
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 1, delay: 0.2 }}
                      sx={{ width: 6, height: 6, bgcolor: 'rgba(255, 255, 255, 0.6)', borderRadius: '50%' }}
                    />
                    <Box
                      component={motion.div}
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ repeat: Infinity, duration: 1, delay: 0.4 }}
                      sx={{ width: 6, height: 6, bgcolor: 'rgba(255, 255, 255, 0.6)', borderRadius: '50%' }}
                    />
                  </Paper>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input Area */}
            <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.2)', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Type a message..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  disabled={messageCount <= 0 || isTyping}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 3,
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&.Mui-focused fieldset': { borderColor: selectedBusiness.color },
                    },
                    '& input::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || messageCount <= 0 || isTyping}
                  sx={{
                    bgcolor: selectedBusiness.color,
                    color: 'white',
                    borderRadius: 3,
                    minWidth: 56,
                    '&:hover': { bgcolor: selectedBusiness.secondaryColor },
                    '&.Mui-disabled': { bgcolor: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.3)' },
                  }}
                >
                  <SendIcon />
                </Button>
              </Box>
              <Typography variant="caption" color="rgba(255, 255, 255, 0.5)" sx={{ display: 'block', mt: 1.5, textAlign: 'center', fontStyle: 'italic' }}>
                These messages are being processed live by an AI agent trained on the business data.
              </Typography>
            </Box>
          </Paper>

          {/* Demo Disclosure */}
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 2.5,
              borderRadius: 3,
              bgcolor: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255, 193, 7, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 0.5,
                }}
              >
                <Typography variant="caption" sx={{ color: '#FFC107', fontWeight: 700 }}>
                  ⚠
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="#FFC107" mb={0.5}>
                  Demo Mode
                </Typography>
                <Typography variant="body2" color="rgba(255, 255, 255, 0.8)" sx={{ lineHeight: 1.6 }}>
                  This is a demonstration of ReachOut's AI capabilities. <strong>No actual payments will be processed</strong> and <strong>no orders will be fulfilled</strong>. 
                  If you request payment or place an order, the AI will explain what would happen in a real scenario, but no transaction will occur.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
